import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "amountInput", "commentInput", "difference", "confirmButton", "invalidValueMessage",
    "unprocessableAmountInput", "unprocessableCommentInput",
    "overpaymentAmountInput", "overpaymentCommentInput", "requiredField"
  ]
  static values = {
    transactionAmount: String,
    currency: { type: String, default: "EUR" },
    remainingLabel: { type: String, default: "Reste à allouer :" },
    overpaymentLabel: { type: String, default: "Trop-perçu :" },
    // An inexact lettering (this form) may only be confirmed by an admin_billing user.
    allowInexact: { type: Boolean, default: true }
  }

  updateDifference() {
    if (!this.hasDifferenceTarget) return

    let diff = this.calculateDiff(this.calculatePaymentSum())
    this.updateModalUI(diff)
  }

  // Unprocessable rows are added/removed by the nested-form controller: recalculate through the
  // target lifecycle instead of chaining actions on its buttons (the removed row is already
  // detached when these fire, so the sums are up to date).
  unprocessableAmountInputTargetConnected() {
    this.updateDifference()
  }

  unprocessableAmountInputTargetDisconnected() {
    this.updateDifference()
  }

  // Overpayment rows are added/removed by the overpayment-rows controller: same lifecycle
  // recalculation as the unprocessable rows above.
  overpaymentAmountInputTargetConnected() {
    this.updateDifference()
  }

  overpaymentAmountInputTargetDisconnected() {
    this.updateDifference()
  }

  updateRow(event) {
    let input = event.target
    let paymentAmount = parseFloat(input.dataset.paymentAmount)
    let enteredAmount = parseFloat(input.value) || 0
    let row = input.closest(".payment-row")

    let commentWrapper = row.querySelector("[data-comment-wrapper]")
    if (enteredAmount > paymentAmount) {
      commentWrapper.classList.remove("d-none")
    } else {
      commentWrapper.classList.add("d-none")
    }

    let balanceEl = row.querySelector("[data-row-balance]")
    let balance = paymentAmount - enteredAmount
    if (balance > 0.001) {
      balanceEl.textContent = `${this.remainingLabelValue} ${this.formatCurrency(balance)}`
      balanceEl.className = "small mb-0 text-success"
    } else if (balance < -0.001) {
      balanceEl.textContent = `${this.overpaymentLabelValue} ${this.formatCurrency(Math.abs(balance))}`
      balanceEl.className = "small mb-0 text-warning"
    } else {
      balanceEl.className = "small mb-0 d-none"
    }
  }

  calculatePaymentSum() {
    return this.allocationInputs().reduce(
      (partialSum, elt) => partialSum + (parseFloat(elt.value) || 0)
    , 0)
  }

  allocationInputs() {
    return [
      ...this.amountInputTargets,
      ...this.unprocessableAmountInputTargets,
      ...this.overpaymentAmountInputTargets
    ]
  }

  calculateDiff(paymentSum) {
    return parseFloat(this.transactionAmountValue) - paymentSum
  }

  invalidValues() {
    return this.allocationInputs().some(input => !(parseFloat(input.value) > 0))
  }

  hasEmptyVisibleComments() {
    return this.commentInputTargets.some(textarea => {
      let wrapper = textarea.closest("[data-comment-wrapper]")
      return !wrapper.classList.contains("d-none") && textarea.value.trim() === ""
    })
  }

  hasEmptyUnprocessableComments() {
    return this.unprocessableCommentInputTargets.some(textarea => textarea.value.trim() === "")
  }

  hasEmptyOverpaymentComments() {
    return this.overpaymentCommentInputTargets.some(textarea => textarea.value.trim() === "")
  }

  // Standalone mandatory fields (e.g. the category select and the global comment of the
  // mark-unprocessable modal): any empty one keeps the confirm button disabled.
  hasEmptyRequiredFields() {
    return this.requiredFieldTargets.some(field => field.value.trim() === "")
  }

  formatCurrency(amount) {
    return amount.toLocaleString('fr-FR', { style: 'currency', currency: this.currencyValue })
  }

  differenceAllowsConfirm(diff) {
    return parseFloat(diff.toFixed(2)) === 0.00
  }

  updateModalUI(diff) {
    this.differenceTarget.innerText = this.formatCurrency(diff)
    let parent = this.differenceTarget.parentElement
    // This form is always an inexact lettering, so only admin_billing users may confirm it.
    if (this.differenceAllowsConfirm(diff) && !this.invalidValues() && !this.hasEmptyVisibleComments() &&
        !this.hasEmptyUnprocessableComments() && !this.hasEmptyOverpaymentComments() &&
        !this.hasEmptyRequiredFields() && this.allowInexactValue) {
      this.confirmButtonTarget.disabled = false
      parent.classList.remove("text-danger")
    } else {
      this.confirmButtonTarget.disabled = true
      parent.classList.add("text-danger")
    }

    if (this.invalidValues()) {
      this.invalidValueMessageTarget.classList.remove("d-none")
    } else {
      this.invalidValueMessageTarget.classList.add("d-none")
    }
  }
}
